/**
 * Tiny, safe fetch helper for the VietClinics public REST API.
 *
 *   Base URL : process.env.REACT_APP_API_URL  (e.g. https://api.vietclinics.com/api/v1)
 *
 * Design goals:
 *   • Never throw — every call returns { ok, data, reason }.
 *   • Always time-bound (default 5s) so a stalled fetch can't freeze the UI.
 *   • No-ops cleanly when REACT_APP_API_URL is missing so preview / static
 *     builds fall back to the bundled treatments.js without any errors.
 */

const RAW_BASE = (process.env.REACT_APP_API_URL || '').trim();
// Strip trailing slash so callers can pass leading-slash paths.
const BASE = RAW_BASE.replace(/\/+$/, '');

export const isApiConfigured = () => Boolean(BASE);

// Surface a single, unmissable warning when the catalog API is unconfigured.
// Fires once per page load in BOTH dev and prod — this is the #1 silent-fail
// signature when `yarn build` ran without REACT_APP_API_URL baked in.
let didWarnMissingApi = false;
if (typeof window !== 'undefined' && !BASE && !didWarnMissingApi) {
  didWarnMissingApi = true;
  // eslint-disable-next-line no-console
  console.warn(
    '[VietClinics] REACT_APP_API_URL is not set — catalog & web-content-overrides '
    + 'API calls are DISABLED. The site is rendering entirely from the bundled '
    + 'src/data/treatments.js and admin image overrides will NOT appear. '
    + 'Set REACT_APP_API_URL=https://api.vietclinics.com/api/v1 in .env.production '
    + 'BEFORE running `yarn build`, then redeploy. See frontend/DEPLOY_VPS_CHECKLIST.md.'
  );
}

export const buildUrl = (path, params) => {
  if (!BASE) return null;
  const cleaned = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${BASE}${cleaned}`, window.location.origin);
  if (params && typeof params === 'object') {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        url.searchParams.append(k, String(v));
      }
    });
  }
  return url.toString();
};

/**
 * GET a public resource. Returns:
 *   { ok: true,  data }
 *   { ok: false, reason: 'no_api' | 'timeout' | 'network' | 'http_<code>' | 'parse' }
 */
export const apiGet = async (path, { params, timeoutMs = 5000, signal } = {}) => {
  if (!isApiConfigured()) {
    return { ok: false, reason: 'no_api' };
  }

  const url = buildUrl(path, params);
  if (!url) return { ok: false, reason: 'no_api' };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  // Compose external signal with our timeout signal.
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });

    if (!res.ok) {
      return { ok: false, reason: `http_${res.status}` };
    }

    try {
      const data = await res.json();
      return { ok: true, data };
    } catch (_parseErr) {
      return { ok: false, reason: 'parse' };
    }
  } catch (err) {
    if (err.name === 'AbortError') {
      return { ok: false, reason: 'timeout' };
    }
    return { ok: false, reason: 'network' };
  } finally {
    clearTimeout(timer);
  }
};
